// src/components/Comments/CommentSection.jsx
import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { fetchComments, postComment, commentVote } from "../../../utils/utils";
import { analyzeSentiment } from "../../../utils/sentiment";
import CommentCard from "./CommentCard";
import VoteRadioGroup from "./VoteRadioGroup";
import { useUser } from "../../../context/UserContext";
import { useNavigate } from "react-router-dom";
import ShieldIcon from "@mui/icons-material/Shield";
import { theme } from "../../../theme/colors";
import { shape } from "../../../theme/shape";

const extractCommentUserId = (comment) => {
  const id = comment?.user_id ?? comment?.userId ?? comment?.user;
  return id != null ? String(id) : null;
};

const CommentSection = ({ billId, dnaUserId }) => {
  const isLoggedIn = !!dnaUserId;
  const userToggledVerified = useRef(false);
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState("");
  const [loading, setLoading] = useState(false);
  const [loadingComments, setLoadingComments] = useState(true);
  const [vote, setVote] = useState("");
  const [sortBy, setSortBy] = useState("likes");
  const [filterBy, setFilterBy] = useState("all");
  const [verifiedOnly, setVerifiedOnly] = useState(() => !isLoggedIn);
  const [currentPage, setCurrentPage] = useState(1);
  const commentsPerPage = 5;

  const [postAnonymously, setPostAnonymously] = useState(true);
  const [voteError, setVoteError] = useState(false);

  const { verifiedUser, userId } = useUser();
  const navigate = useNavigate();

  const currentUserIds = useMemo(
    () => [userId, dnaUserId].filter(Boolean).map(String),
    [userId, dnaUserId]
  );

  const isOwnComment = useCallback(
    (comment) => {
      const ownerId = extractCommentUserId(comment);
      return ownerId && currentUserIds.includes(ownerId);
    },
    [currentUserIds]
  );

  useEffect(() => {
    if (userToggledVerified.current) return;
    setVerifiedOnly(isLoggedIn);
  }, [isLoggedIn]);

  const loadComments = useCallback(async () => {
    setLoadingComments(true);
    try {
      const data = await fetchComments(billId, { verifiedOnly });
      setComments(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Failed to load comments", err);
    } finally {
      setLoadingComments(false);
    }
  }, [billId, verifiedOnly]);

  useEffect(() => {
    loadComments();
  }, [loadComments]);

  const handleCommentSubmit = async (e) => {
    e.preventDefault();

    if (!newComment.trim()) return;

    if (!vote) {
      setVoteError(true);
      return;
    }
    setVoteError(false);

    setLoading(true);
    try {
      await analyzeSentiment(newComment);

      const created = await postComment(
        billId,
        newComment.trim(),
        vote.toUpperCase(),
        postAnonymously
      );

      setComments((prev) => [created, ...prev]);
      setNewComment("");
      setCurrentPage(1);
    } catch (err) {
      console.error("Error posting comment:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleVote = async (commentId, type) => {
    try {
      const data = await commentVote(commentId, type);
      setComments((prev) =>
        prev.map((c) =>
          c.id === commentId
            ? { ...c, likes: data.likes, dislikes: data.dislikes }
            : c
        )
      );
    } catch (err) {
      console.error("Error voting on comment:", err);
    }
  };

  const filteredAndSorted = useMemo(() => {
    // The API already filters when the toggle is on; this is a client-side
    // guard for comments added locally. An absent flag means "unknown", so
    // only hide authors the API explicitly reports as unverified.
    const verifiedFiltered = verifiedOnly
      ? comments.filter(
          (c) => c.author_is_verified !== false || isOwnComment(c)
        )
      : comments;

    const base =
      filterBy === "all"
        ? verifiedFiltered
        : verifiedFiltered.filter((c) => c.vote === filterBy);

    const sorted = [...base].sort((a, b) => {
      if (sortBy === "newest") {
        return new Date(b.created_at) - new Date(a.created_at);
      }
      if (sortBy === "oldest") {
        return new Date(a.created_at) - new Date(b.created_at);
      }
      return (b.likes || 0) - (a.likes || 0);
    });

    return sorted;
  }, [comments, filterBy, sortBy, verifiedOnly, isOwnComment]);

  const totalPages = Math.max(
    1,
    Math.ceil(filteredAndSorted.length / commentsPerPage)
  );
  const startIdx = (currentPage - 1) * commentsPerPage;
  const paginatedComments = filteredAndSorted.slice(
    startIdx,
    startIdx + commentsPerPage
  );

  const goToVerify = () => navigate("/verify-identity");

  return (
    <section className="w-full">
      <hr className="my-6 h-px w-full border-0 bg-slate-200" />

      <div className="mb-4 flex flex-wrap items-end justify-between gap-3">
        <h3 className="text-lg font-semibold text-slate-900">Comments</h3>

        <div className="flex flex-wrap items-center gap-2">
          <label className="inline-flex min-h-[44px] cursor-pointer items-center gap-2 text-sm text-slate-700">
            <input
              type="checkbox"
              className="sr-only"
              checked={verifiedOnly}
              onChange={() => {
                userToggledVerified.current = true;
                setVerifiedOnly((v) => !v);
                setCurrentPage(1);
              }}
              aria-label="Verified users only"
            />
            <span
              className={`inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-lg border-2 transition-colors ${
                verifiedOnly
                  ? "border-[#047857] bg-[#047857]"
                  : "border-slate-300 bg-white"
              }`}
              aria-hidden="true"
            >
              <svg
                className={`h-3.5 w-3.5 text-white transition-opacity ${
                  verifiedOnly ? "opacity-100" : "opacity-0"
                }`}
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="3"
                aria-hidden="true"
              >
                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
              </svg>
            </span>
            Verified users only
          </label>

          <span className="mx-1 hidden h-4 w-px bg-slate-200 sm:block" />

          <div className="inline-flex min-h-[44px] items-center gap-1.5">
            <label htmlFor="comment-sort" className="text-sm text-slate-600">
              Sort
            </label>
            <select
              id="comment-sort"
              value={sortBy}
              onChange={(e) => {
                setSortBy(e.target.value);
                setCurrentPage(1);
              }}
              className="appearance-none rounded-lg border border-slate-300 bg-[#f8fafc] bg-[url('data:image/svg+xml;charset=US-ASCII,%3Csvg%20width%3D%2220%22%20height%3D%2220%22%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20viewBox%3D%220%200%2020%2020%22%20fill%3D%22currentColor%22%3E%3Cpath%20fill-rule%3D%22evenodd%22%20d%3D%22M5.293%207.293a1%201%200%20011.414%200L10%2010.586l3.293-3.293a1%201%200%20111.414%201.414l-4%204a1%201%200%2001-1.414%200l-4-4a1%201%200%20010-1.414z%22%20clip-rule%3D%22evenodd%22%2F%3E%3C%2Fsvg%3E')] bg-[length:1.25rem_1.25rem] bg-[right_0.5rem_center] bg-no-repeat py-2 pl-3 pr-8 text-sm text-slate-900 focus:border-[#047857] focus:outline-none focus:ring-1 focus:ring-[#047857]"
              style={{ borderRadius: shape.radius.control }}
            >
              <option value="likes">Most Liked</option>
              <option value="newest">Newest</option>
              <option value="oldest">Oldest</option>
            </select>
          </div>

          <span className="mx-1 hidden h-4 w-px bg-slate-200 sm:block" />

          <div className="inline-flex min-h-[44px] items-center gap-1.5">
            <label htmlFor="comment-filter" className="text-sm text-slate-600">
              Filter
            </label>
            <select
              id="comment-filter"
              value={filterBy}
              onChange={(e) => {
                setFilterBy(e.target.value);
                setCurrentPage(1);
              }}
              className="appearance-none rounded-lg border border-slate-300 bg-[#f8fafc] bg-[url('data:image/svg+xml;charset=US-ASCII,%3Csvg%20width%3D%2220%22%20height%3D%2220%22%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20viewBox%3D%220%200%2020%2020%22%20fill%3D%22currentColor%22%3E%3Cpath%20fill-rule%3D%22evenodd%22%20d%3D%22M5.293%207.293a1%201%200%20011.414%200L10%2010.586l3.293-3.293a1%201%200%20111.414%201.414l-4%204a1%201%200%2001-1.414%200l-4-4a1%201%200%20010-1.414z%22%20clip-rule%3D%22evenodd%22%2F%3E%3C%2Fsvg%3E')] bg-[length:1.25rem_1.25rem] bg-[right_0.5rem_center] bg-no-repeat py-2 pl-3 pr-8 text-sm text-slate-900 focus:border-[#047857] focus:outline-none focus:ring-1 focus:ring-[#047857]"
              style={{ borderRadius: shape.radius.control }}
            >
              <option value="all">All Votes</option>
              <option value="SUPPORT">Support</option>
              <option value="OPPOSE">Oppose</option>
              <option value="NEEDS_REVISIONS">Support with amendments</option>
              <option value="UNDECIDED">Undecided</option>
            </select>
          </div>
        </div>
      </div>

      {dnaUserId ? (
        <form
          onSubmit={handleCommentSubmit}
          className="mb-6 rounded-xl border border-slate-200 bg-white p-4 shadow-sm"
          style={{ borderRadius: shape.radius.card }}
        >
          {!verifiedUser && (
            <button
              type="button"
              onClick={goToVerify}
              className="mb-3 flex w-full cursor-pointer flex-wrap items-center gap-x-3 gap-y-2 rounded-lg border border-[#047857]/25 bg-[#ecfdf5] px-3 py-2.5 text-left transition-colors hover:bg-[#d1fae5] focus:outline-none focus:ring-2 focus:ring-[#047857]/30"
              style={{ borderRadius: shape.radius.control }}
            >
              <div className="flex min-w-0 flex-1 items-center gap-3">
                <span className="inline-flex shrink-0 items-center justify-center self-center leading-none">
                  <ShieldIcon
                    sx={{ fontSize: 20, color: theme.primary, display: "block" }}
                    aria-hidden
                  />
                </span>
                <span className="min-w-0 flex-1 text-[15px] leading-normal text-[#0f172a] sm:text-base">
                  Credibility is key to our mission. Identity verification helps build trust in civic
                  discussion.
                </span>
              </div>
              <span
                className="shrink-0 rounded-md px-3 py-1.5 text-xs font-semibold text-white sm:text-sm"
                style={{ backgroundColor: theme.primary, borderRadius: shape.radius.control }}
                aria-hidden="true"
              >
                Get Verified
              </span>
            </button>
          )}

          <VoteRadioGroup
            vote={vote}
            setVote={setVote}
            voteError={voteError}
            disabled={loading}
          />

          <textarea
            value={newComment}
            onChange={(e) => setNewComment(e.target.value)}
            placeholder="Share your thoughts on this bill…"
            rows={4}
            className="mt-2 w-full rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 shadow-sm focus:outline-none focus:ring-4 focus:ring-[#047857]/20 disabled:bg-slate-50 disabled:text-slate-500"
            style={{ borderRadius: shape.radius.control }}
            required
            disabled={loading}
          />

          <div className="mt-3 flex flex-col items-start justify-between gap-3 sm:flex-row sm:items-center">
            <label className="inline-flex items-center gap-2 text-sm text-slate-700">
              <input
                type="checkbox"
                className="h-4 w-4 rounded border-slate-300 text-[#0e918c] focus:ring-[#0e918c]"
                checked={postAnonymously}
                onChange={() => setPostAnonymously((v) => !v)}
              />
              Post as anonymous
            </label>

            <button
              type="submit"
              disabled={loading}
              className="inline-flex items-center justify-center rounded-full px-5 py-2 text-sm font-semibold text-white hover:brightness-95 focus:outline-none focus:ring-4 focus:ring-[#047857]/30 disabled:opacity-60"
              style={{ backgroundColor: theme.primary, borderRadius: shape.radius.control }}
            >
              {loading ? "Posting…" : "Post Comment"}
            </button>
          </div>
        </form>
      ) : (
        <div
          className="mb-6 inline-flex w-auto max-w-full items-center rounded-xl border border-slate-200 bg-white px-4 py-2 shadow-sm"
          style={{ borderRadius: shape.radius.card }}
        >
          <p className="m-0 text-base leading-snug text-slate-700">
            Create a free account to comment and vote on discussion.{" "}
            <button
              type="button"
              className="rounded px-1 py-0.5 font-bold underline transition-colors hover:bg-[#047857]/10"
              style={{ color: theme.primary }}
              onClick={() => (window.location.href = "/auth/signup")}
            >
              Sign up
            </button>
          </p>
        </div>
      )}

      <div className="space-y-5">
        {loadingComments ? (
          <p className="py-6 text-center text-sm text-slate-600">Loading comments…</p>
        ) : paginatedComments.length > 0 ? (
          paginatedComments.map((comment) => (
            <CommentCard
              key={comment.id}
              comment={comment}
              userId={dnaUserId}
              verifiedOnly={verifiedOnly}
              onVote={handleVote}
              onDelete={(deletedId) =>
                setComments((prev) => prev.filter((c) => c.id !== deletedId))
              }
            />
          ))
        ) : (
          <p className="py-6 text-center text-sm text-slate-900">
            {verifiedOnly
              ? "No verified comments yet."
              : "No comments yet. Be the first to comment!"}
          </p>
        )}
      </div>

      {totalPages > 1 && (
        <div className="mt-6 flex items-center justify-center gap-4">
          <button
            onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
            disabled={currentPage === 1}
            className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-slate-300 bg-white text-slate-700 hover:bg-slate-50 disabled:opacity-50"
            style={{ borderRadius: shape.radius.control }}
            aria-label="Previous page"
          >
            ‹
          </button>
          <span className="text-sm text-slate-700">
            Page <span className="font-semibold">{currentPage}</span> of{" "}
            <span className="font-semibold">{totalPages}</span>
          </span>
          <button
            onClick={() => setCurrentPage((p) => Math.min(p + 1, totalPages))}
            disabled={currentPage === totalPages}
            className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-slate-300 bg-white text-slate-700 hover:bg-slate-50 disabled:opacity-50"
            style={{ borderRadius: shape.radius.control }}
            aria-label="Next page"
          >
            ›
          </button>
        </div>
      )}
    </section>
  );
};

export default CommentSection;
